import type {
	QuestionnaireQuestion,
	QuestionnaireAnswerMetadata,
	QuestionnaireRiskLevel,
	QuestionnaireSource,
} from "../contracts"

const MALE_PDF = "ARORA MEN'S INITIAL HRT INTAKE FORM.pdf"
const FEMALE_PDF = "ARORA FEMALE INITIAL HRT INTAKE FORM.pdf"

type AroraQuestionSource = Omit<QuestionnaireQuestion, "options" | "answerMetadata"> & {
	readonly options: readonly string[]
	readonly yellowAnswers?: readonly string[]
}

function source(
	pdf: string,
	page: number,
	section: string,
	questionNumber: string,
): QuestionnaireSource {
	return { pdf, page, section, questionNumber }
}

function riskForAnswer(entry: AroraQuestionSource, option: string): QuestionnaireRiskLevel {
	if (entry.disqualifyingAnswers !== undefined && entry.disqualifyingAnswers.includes(option)) {
		return "red"
	}
	if (entry.yellowAnswers !== undefined && entry.yellowAnswers.includes(option)) {
		return "yellow"
	}
	return "regular"
}

function toQuestion(entry: AroraQuestionSource): QuestionnaireQuestion {
	const answerMetadata: Record<string, QuestionnaireAnswerMetadata> = {}
	for (const option of entry.options) {
		const riskLevel = riskForAnswer(entry, option)
		answerMetadata[option] = {
			riskLevel,
			source: entry.source,
			...(riskLevel === "red" ? { disqualifies: true } : {}),
		}
	}

	return {
		id: entry.id,
		question: entry.question,
		...(entry.subtitle === undefined ? {} : { subtitle: entry.subtitle }),
		type: entry.type,
		options: [...entry.options],
		phase: entry.phase,
		gender: entry.gender,
		riskLevel: entry.riskLevel,
		source: entry.source,
		...(Object.keys(answerMetadata).length === 0 ? {} : { answerMetadata }),
		...(entry.disqualifyingAnswers === undefined ? {} : { disqualifyingAnswers: [...entry.disqualifyingAnswers] }),
		...(entry.detailsPrompt === undefined ? {} : { detailsPrompt: entry.detailsPrompt }),
		...(entry.detailsRequiredForAnswers === undefined
			? {}
			: { detailsRequiredForAnswers: [...entry.detailsRequiredForAnswers] }),
	}
}

export const aroraMaleHrtQuestionSource = [
	{
		id: "male_primary_goals",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 1, "Section 2: Goals & Symptoms", "2.1"),
		question: "What are your primary goals for seeking hormone therapy? (Select all that apply)",
		type: "multiselect",
		options: [
			"Improve energy / reduce fatigue",
			"Improve libido / sexual function",
			"Improve mood / motivation",
			"Improve sleep",
			"Improve body composition / muscle mass",
			"Improve focus / reduce brain fog",
			"Other",
		],
		detailsPrompt: "Other: ______________________________",
		detailsRequiredForAnswers: ["Other"],
	},
	{
		id: "male_current_symptoms",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 1, "Section 2: Goals & Symptoms", "2.2"),
		question: "Which symptoms are you currently experiencing? (Select all that apply)",
		type: "multiselect",
		options: [
			"Low libido or erectile dysfunction",
			"Fatigue / low energy",
			"Depressed mood or irritability",
			"Poor concentration or brain fog",
			"Loss of muscle or increased body fat",
			"Poor sleep quality",
			"Night sweats",
			"None of the above",
		],
	},
	{
		id: "male_prostate_or_breast_cancer",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 1, "Section 3: Medical History", "3.1"),
		question: "Do you currently have active or untreated prostate cancer or male breast cancer?",
		type: "select",
		options: [
			"No",
			"Yes — I have active or untreated prostate cancer or male breast cancer",
			"History of prostate cancer or male breast cancer — treated and/or in remission",
		],
		disqualifyingAnswers: ["Yes — I have active or untreated prostate cancer or male breast cancer"],
		yellowAnswers: ["History of prostate cancer or male breast cancer — treated and/or in remission"],
	},
	{
		id: "male_cardiovascular_or_clot_history",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 2, "Section 3: Medical History", "3.2"),
		question: "Have you had any of the following? (Select all that apply)",
		type: "multiselect",
		options: [
			"Heart attack, stroke, or blood clot within the past 6 months",
			"History of heart attack, stroke, or blood clot more than 6 months ago",
			"Known clotting disorder or inherited thrombophilia (e.g., Factor V Leiden, antiphospholipid syndrome)",
			"None of the above",
		],
		disqualifyingAnswers: ["Heart attack, stroke, or blood clot within the past 6 months"],
		yellowAnswers: [
			"History of heart attack, stroke, or blood clot more than 6 months ago",
			"Known clotting disorder or inherited thrombophilia (e.g., Factor V Leiden, antiphospholipid syndrome)",
		],
	},
	{
		id: "male_blood_pressure_or_cardiovascular_disease",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 2, "Section 3: Medical History", "3.3"),
		question: "Do you have any of the following? (Select all that apply)",
		type: "multiselect",
		options: [
			"Uncontrolled high blood pressure — consistently above 160/100 despite medication",
			"Active severe cardiovascular disease — e.g., unstable angina or decompensated heart failure",
			"High blood pressure that is currently controlled or managed with medication",
			"None of the above",
		],
		disqualifyingAnswers: [
			"Uncontrolled high blood pressure — consistently above 160/100 despite medication",
			"Active severe cardiovascular disease — e.g., unstable angina or decompensated heart failure",
		],
		yellowAnswers: ["High blood pressure that is currently controlled or managed with medication"],
	},
	{
		id: "male_sleep_apnea",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 3, "Section 3: Medical History", "3.4"),
		question: "Have you been diagnosed with sleep apnea?",
		type: "select",
		options: [
			"No — no sleep apnea diagnosis",
			"Yes — severe obstructive sleep apnea, currently untreated or without CPAP therapy",
			"Yes — sleep apnea currently treated with CPAP or other prescribed therapy",
		],
		disqualifyingAnswers: ["Yes — severe obstructive sleep apnea, currently untreated or without CPAP therapy"],
		yellowAnswers: ["Yes — sleep apnea currently treated with CPAP or other prescribed therapy"],
	},
	{
		id: "male_liver_disease",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 3, "Section 3: Medical History", "3.5"),
		question: "Do you have severe liver disease or significant liver dysfunction?",
		type: "select",
		options: [
			"No",
			"Yes — severe or active liver disease (e.g., cirrhosis, hepatic failure, active hepatitis)",
			"Yes — mild to moderate liver condition, currently managed",
		],
		disqualifyingAnswers: ["Yes — severe or active liver disease (e.g., cirrhosis, hepatic failure, active hepatitis)"],
		yellowAnswers: ["Yes — mild to moderate liver condition, currently managed"],
	},
	{
		id: "male_kidney_disease",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 4, "Section 3: Medical History", "3.6"),
		question: "Do you have severe kidney disease or significant renal dysfunction?",
		type: "select",
		options: [
			"No",
			"Yes — severe chronic kidney disease (CKD Stage 4–5) or kidney failure",
			"Yes — mild to moderate kidney disease, currently managed",
		],
		disqualifyingAnswers: ["Yes — severe chronic kidney disease (CKD Stage 4–5) or kidney failure"],
		yellowAnswers: ["Yes — mild to moderate kidney disease, currently managed"],
	},
	{
		id: "male_hematocrit_polycythemia",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 4, "Section 3: Medical History", "3.7"),
		question: "Have you been diagnosed with high hematocrit, polycythemia, or required therapeutic phlebotomy?",
		type: "select",
		options: [
			"No",
			"Yes — currently elevated hematocrit or actively requiring treatment or phlebotomy",
			"Yes — history of elevated hematocrit or polycythemia, now resolved",
		],
		disqualifyingAnswers: ["Yes — currently elevated hematocrit or actively requiring treatment or phlebotomy"],
		yellowAnswers: ["Yes — history of elevated hematocrit or polycythemia, now resolved"],
	},
	{
		id: "male_other_cancer",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 5, "Section 3: Medical History", "3.8"),
		question: "Have you ever been diagnosed with any other cancer (excluding prostate or male breast cancer)?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, cancer type, year of diagnosis, and current status:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "male_diabetes_kidney_liver_autoimmune",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 5, "Section 3: Medical History", "3.9"),
		question: "Have you been diagnosed with diabetes, kidney disease (not already answered above), liver disease (not already answered above), or an autoimmune disease?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, condition(s) and current management / medications:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "male_thyroid_disease",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 5, "Section 3: Medical History", "3.10"),
		question: "Have you been diagnosed with thyroid disease (hypothyroid, hyperthyroid, Hashimoto’s, or Graves’ disease)?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, thyroid diagnosis, current medication, and last TSH value if known:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "male_psa_or_prostate_symptoms",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 5, "Section 3: Medical History", "3.11"),
		question: "Have you had an elevated PSA, enlarged prostate, or urinary obstruction symptoms?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ Most recent PSA value and date (if known):\n➤ PSA value and date:\n______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "male_other_medical_conditions",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 3: Medical History", "3.12"),
		question: "Are there any other medical conditions your provider should be aware of before considering hormone therapy?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, please briefly describe:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "male_testosterone_or_hormone_history",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 4: Hormone & Medication History", "4.1"),
		question: "Have you ever used testosterone, anabolic steroids, or hormone therapy?",
		type: "select",
		options: ["Never", "Prior use only", "Currently using"],
	},
	{
		id: "male_adjunctive_medications",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 4: Hormone & Medication History", "4.2"),
		question: "Have you ever used any of the following adjunctive medications? (Select all that apply)",
		type: "multiselect",
		options: [
			"Enclomiphene or clomiphene (Clomid)",
			"hCG (human chorionic gonadotropin)",
			"Gonadorelin",
			"Aromatase inhibitor (anastrozole, exemestane)",
			"DHEA supplements",
			"None of the above",
		],
		detailsPrompt: "➤ If yes, dose and reason for use:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: [
			"Enclomiphene or clomiphene (Clomid)",
			"hCG (human chorionic gonadotropin)",
			"Gonadorelin",
			"Aromatase inhibitor (anastrozole, exemestane)",
			"DHEA supplements",
		],
	},
	{
		id: "male_current_prescription_medications",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 4: Hormone & Medication History", "4.3"),
		question: "Please list any current prescription medications:",
		type: "text",
		options: [],
	},
	{
		id: "male_medication_or_injectable_allergies",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 4: Hormone & Medication History", "4.4"),
		question: "Do you have any medication or injectable allergies (including reactions to compounded medications or carrier oils)?",
		type: "select",
		options: ["No known allergies", "Yes — please list below"],
		detailsPrompt: "➤ Medication / substance and reaction description:\n______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes — please list below"],
	},
	{
		id: "male_lab_history",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 6, "Section 5: Laboratory History", "5.1"),
		question: "Have you had hormone or blood labs drawn within the past 3–6 months?",
		type: "select",
		options: [
			"Yes — labs have been completed recently",
			"No — I have not had recent labs",
			"Labs from more than 6 months ago",
		],
	},
	{
		id: "male_future_children",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(MALE_PDF, 7, "Section 6: Fertility", "6.1"),
		question: "Do you wish to have children in the future?",
		type: "select",
		options: ["Yes", "No", "Unsure"],
		yellowAnswers: ["Yes", "Unsure"],
	},
	{
		id: "male_nicotine_or_tobacco",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(MALE_PDF, 7, "Section 7: Behavioral Health & Substance Use", "7.1"),
		question: "Do you currently use nicotine or tobacco products?",
		type: "select",
		options: ["Yes", "No"],
		yellowAnswers: ["Yes"],
	},
	{
		id: "male_alcohol_or_substance_history",
		gender: "male",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(MALE_PDF, 7, "Section 7: Behavioral Health & Substance Use", "7.2"),
		question: "Do you have heavy alcohol use or a history of substance use disorder?",
		type: "select",
		options: ["Yes", "No"],
	},
	{
		id: "male_psychiatric_history",
		gender: "male",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(MALE_PDF, 8, "Section 7: Behavioral Health & Substance Use", "7.3"),
		question: "Do you currently have any of the following? (Select all that apply)",
		type: "multiselect",
		options: [
			"Active psychosis or psychotic symptoms",
			"Uncontrolled bipolar disorder or a current manic episode",
			"Stable psychiatric condition currently managed under treatment",
			"None of the above",
		],
		disqualifyingAnswers: [
			"Active psychosis or psychotic symptoms",
			"Uncontrolled bipolar disorder or a current manic episode",
		],
		yellowAnswers: ["Stable psychiatric condition currently managed under treatment"],
	},
] as const satisfies readonly AroraQuestionSource[]

export const aroraFemaleHrtQuestionSource = [
	{
		id: "female_primary_goals",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 1, "Section 2: Goals & Symptoms", "2.1"),
		question: "What are your primary goals for seeking hormone therapy? (Select all that apply)",
		type: "multiselect",
		options: [
			"Improve energy / reduce fatigue",
			"Improve mood / emotional stability",
			"Improve sleep",
			"Improve libido / sexual function",
			"Reduce perimenopause or menopause symptoms (hot flashes, night sweats)",
			"Improve body composition / weight concerns",
			"Improve focus / reduce brain fog",
			"Reduce vaginal dryness or genitourinary symptoms",
			"Other",
		],
		detailsPrompt: "Other: ______________________________",
		detailsRequiredForAnswers: ["Other"],
	},
	{
		id: "female_current_symptoms",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 1, "Section 2: Goals & Symptoms", "2.2"),
		question: "Which symptoms are you currently experiencing? (Select all that apply)",
		type: "multiselect",
		options: [
			"Hot flashes or night sweats",
			"Fatigue / low energy",
			"Mood changes, anxiety, or irritability",
			"Poor sleep quality",
			"Brain fog or memory issues",
			"Low libido",
			"Weight gain or body composition changes",
			"Irregular or absent menstrual cycles",
			"Vaginal dryness, pain with intercourse, or recurrent urinary tract infections",
			"None of the above",
		],
	},
	{
		id: "female_menstrual_periods",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 1, "Section 3: Menstrual & Reproductive History", "3.1"),
		question: "Do you currently have menstrual periods?",
		type: "select",
		options: [
			"Yes — cycles are regular",
			"Yes — cycles are irregular",
			"No — periods have stopped",
			"Unsure",
		],
	},
	{
		id: "female_postmenopausal",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 2, "Section 3: Menstrual & Reproductive History", "3.2"),
		question: "Are you postmenopausal (no menstrual period for 12 or more months)?",
		type: "select",
		options: ["Yes", "No", "Unsure"],
		detailsPrompt: "➤ If yes, approximate age at menopause:\nAge at menopause: ____________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "female_pregnancy_breastfeeding_conception",
		gender: "female",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(FEMALE_PDF, 2, "Section 3: Menstrual & Reproductive History", "3.3"),
		question: "Are you currently pregnant, breastfeeding, or actively trying to conceive?",
		type: "select",
		options: [
			"No",
			"Yes — currently pregnant",
			"Yes — currently breastfeeding",
			"Yes — actively trying to conceive",
		],
		disqualifyingAnswers: [
			"Yes — currently pregnant",
			"Yes — currently breastfeeding",
			"Yes — actively trying to conceive",
		],
	},
	{
		id: "female_uterus",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "info",
		source: source(FEMALE_PDF, 2, "Section 3: Menstrual & Reproductive History", "3.4"),
		question: "Do you have a uterus? (This determines whether progesterone is required)",
		type: "select",
		options: ["Yes — uterus intact", "No — hysterectomy (uterus removed)", "Unsure"],
	},
	{
		id: "female_hormone_sensitive_cancer",
		gender: "female",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(FEMALE_PDF, 2, "Section 4: Medical History", "4.1"),
		question: "Have you ever been diagnosed with breast cancer, ovarian cancer, uterine/endometrial cancer, or another estrogen-sensitive cancer?",
		type: "select",
		options: [
			"No",
			"Yes — currently active or under active treatment",
			"Yes — history of hormone-receptor-positive cancer, treated and in remission",
		],
		disqualifyingAnswers: ["Yes — currently active or under active treatment"],
		yellowAnswers: ["Yes — history of hormone-receptor-positive cancer, treated and in remission"],
	},
	{
		id: "female_other_cancer",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 3, "Section 4: Medical History", "4.2"),
		question: "Have you ever been diagnosed with any other cancer (not listed above)?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, cancer type, year of diagnosis, and current status:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "female_clot_stroke_heart_or_clotting_disorder",
		gender: "female",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(FEMALE_PDF, 3, "Section 4: Medical History", "4.3"),
		question: "Have you ever had a blood clot (DVT or pulmonary embolism), stroke, or heart attack?\nHave you been diagnosed with a clotting disorder?",
		type: "multiselect",
		options: [
			"No",
			"Yes — active or recent DVT or pulmonary embolism (within the past 6 months)",
			"Yes — history of DVT, PE, stroke, or heart attack more than 6 months ago",
			"Yes — known inherited clotting disorder (e.g., Factor V Leiden, antiphospholipid syndrome)",
		],
		disqualifyingAnswers: ["Yes — active or recent DVT or pulmonary embolism (within the past 6 months)"],
		yellowAnswers: [
			"Yes — history of DVT, PE, stroke, or heart attack more than 6 months ago",
			"Yes — known inherited clotting disorder (e.g., Factor V Leiden, antiphospholipid syndrome)",
		],
	},
	{
		id: "female_blood_pressure_or_heart_disease",
		gender: "female",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(FEMALE_PDF, 4, "Section 4: Medical History", "4.4"),
		question: "Do you have uncontrolled high blood pressure or significant heart disease?",
		type: "multiselect",
		options: [
			"No",
			"Yes — blood pressure is controlled with medication",
			"Yes — blood pressure is uncontrolled (consistently above 160/100)",
			"Yes — active significant heart disease (e.g., recent MI, unstable angina, decompensated heart failure)",
		],
		disqualifyingAnswers: [
			"Yes — blood pressure is uncontrolled (consistently above 160/100)",
			"Yes — active significant heart disease (e.g., recent MI, unstable angina, decompensated heart failure)",
		],
		yellowAnswers: ["Yes — blood pressure is controlled with medication"],
	},
	{
		id: "female_migraine_with_aura",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 4, "Section 4: Medical History", "4.5"),
		question: "Have you been diagnosed with migraines with aura?",
		type: "select",
		options: ["No", "Yes — migraine without aura", "Yes — migraine with aura"],
		yellowAnswers: ["Yes — migraine with aura"],
	},
	{
		id: "female_unexplained_vaginal_bleeding",
		gender: "female",
		phase: "hard_eligibility",
		riskLevel: "red",
		source: source(FEMALE_PDF, 4, "Section 4: Medical History", "4.6"),
		question: "Have you been diagnosed with unexplained vaginal bleeding?",
		type: "select",
		options: [
			"No",
			"Yes — unexplained vaginal bleeding that has not yet been evaluated",
			"Yes — vaginal bleeding has been evaluated and a cause has been identified",
		],
		disqualifyingAnswers: ["Yes — unexplained vaginal bleeding that has not yet been evaluated"],
	},
	{
		id: "female_diabetes_kidney_liver_autoimmune",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 5, "Section 4: Medical History", "4.7"),
		question: "Have you been diagnosed with diabetes, kidney disease, liver disease, or an autoimmune disease?",
		type: "select",
		options: ["Yes", "No"],
		yellowAnswers: ["Yes"],
		detailsPrompt: "➤ If yes, please list condition(s) and current management:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "female_thyroid_disease",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 5, "Section 4: Medical History", "4.8"),
		question: "Have you been diagnosed with thyroid disease (hypothyroid, hyperthyroid, Hashimoto’s, or Graves’ disease)?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, thyroid diagnosis, current medication, and last TSH value if known:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "female_pcos_endometriosis_fibroids_bleeding",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 5, "Section 4: Medical History", "4.9"),
		question: "Have you been diagnosed with polycystic ovary syndrome (PCOS), endometriosis, uterine fibroids, or abnormal uterine bleeding?",
		type: "multiselect",
		options: [
			"No",
			"Yes — PCOS",
			"Yes — endometriosis",
			"Yes — uterine fibroids",
			"Yes — abnormal uterine bleeding (evaluated)",
		],
		yellowAnswers: ["Yes — PCOS", "Yes — endometriosis", "Yes — uterine fibroids", "Yes — abnormal uterine bleeding (evaluated)"],
	},
	{
		id: "female_hormonal_contraception",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 5, "Section 4: Medical History", "4.10"),
		question: "Are you currently using hormonal contraception (birth control pills, IUD with hormones, patch, ring, implant, or injection)?",
		type: "select",
		options: ["No", "Yes — currently using hormonal contraception"],
		yellowAnswers: ["Yes — currently using hormonal contraception"],
	},
	{
		id: "female_other_medical_conditions",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 5, "Section 4: Medical History", "4.11"),
		question: "Are there any other medical conditions your clinician should be aware of before considering hormone therapy?",
		type: "select",
		options: ["Yes", "No"],
		detailsPrompt: "➤ If yes, please briefly describe:\n➤ Details: ______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes"],
	},
	{
		id: "female_hormone_therapy_history",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 6, "Section 5: Hormone & Medication History", "5.1"),
		question: "Have you ever used hormone therapy (estrogen, progesterone, testosterone, or compounded hormone therapy)?",
		type: "select",
		options: ["Never", "Prior use only", "Currently using"],
		detailsPrompt: "➤ If prior or current use, please list type, dose, route, and duration:\n➤ Hormone type / dose / route / duration:\n______________________________________________________________________",
		detailsRequiredForAnswers: ["Prior use only", "Currently using"],
	},
	{
		id: "female_current_prescription_medications",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 6, "Section 5: Hormone & Medication History", "5.2"),
		question: "Please list any current prescription medications:",
		type: "text",
		options: [],
	},
	{
		id: "female_medication_or_injectable_allergies",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 6, "Section 5: Hormone & Medication History", "5.3"),
		question: "Do you have any medication or injectable allergies (including reactions to compounded medications or carrier oils)?",
		type: "select",
		options: ["No known allergies", "Yes — please list below"],
		detailsPrompt: "➤ Medication / substance and reaction description:\n______________________________________________________________________",
		detailsRequiredForAnswers: ["Yes — please list below"],
	},
	{
		id: "female_lab_history",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 7, "Section 6: Laboratory History", "6.1"),
		question: "Have you had hormone labs drawn within the past 3–6 months?",
		type: "select",
		options: [
			"Yes — labs have been completed recently",
			"No — I have not had recent labs",
			"I have labs from more than 6 months ago",
		],
	},
	{
		id: "female_future_children",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 7, "Section 7: Fertility", "7.1"),
		question: "Do you wish to have children in the future?",
		type: "select",
		options: ["Yes", "No", "Unsure"],
		yellowAnswers: ["Yes", "Unsure"],
	},
	{
		id: "female_nicotine_or_tobacco",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 7, "Section 8: Behavioral Health & Substance Use", "8.1"),
		question: "Do you currently use nicotine or tobacco products?",
		type: "select",
		options: ["Yes", "No"],
		yellowAnswers: ["Yes"],
	},
	{
		id: "female_alcohol_or_substance_history",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "regular",
		source: source(FEMALE_PDF, 7, "Section 8: Behavioral Health & Substance Use", "8.2"),
		question: "Do you have heavy alcohol use or a history of substance use disorder?",
		type: "select",
		options: ["Yes", "No"],
	},
	{
		id: "female_severe_psychiatric_illness",
		gender: "female",
		phase: "clinical_review",
		riskLevel: "yellow",
		source: source(FEMALE_PDF, 8, "Section 8: Behavioral Health & Substance Use", "8.3"),
		question: "Have you been diagnosed with a severe psychiatric illness (such as bipolar disorder or psychosis)?",
		type: "select",
		options: ["No", "Yes — currently active or unstable", "Yes — stable and managed under treatment"],
		yellowAnswers: ["Yes — currently active or unstable", "Yes — stable and managed under treatment"],
	},
] as const satisfies readonly AroraQuestionSource[]

export const aroraHrtQuestionSource = [
	...aroraMaleHrtQuestionSource,
	...aroraFemaleHrtQuestionSource,
] as const satisfies readonly AroraQuestionSource[]

export const aroraHrtQuestions = aroraHrtQuestionSource.map(toQuestion)
